import {
  Image,
  Linking,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import Feather from '@expo/vector-icons/Feather';

import { AvailabilityIndicator } from './availability-indicator';
import PROFILE_IMAGE from '../../assets/profile/alex-256.jpg';
import { colors } from '../theme';
import { inter400, inter500, isWeb } from '../utils/platform';

type HomeHeaderProps = {
  email: string;
  isEmailCopied: boolean;
  isMobile: boolean;
  linkedInUrl: string;
  location: string;
  name: string;
  onCopyEmail: () => void;
  onPressDownloadCv: () => void;
  role: string;
};

export function HomeHeader({
  email,
  isEmailCopied,
  isMobile,
  linkedInUrl,
  location,
  name,
  onCopyEmail,
  onPressDownloadCv,
  role,
}: HomeHeaderProps) {
  const onPressLinkedIn = async () => {
    await Linking.openURL(linkedInUrl);
  };

  return (
    <View style={styles.header}>
      <View style={[styles.mobileIntro, !isMobile && styles.mobileIntroHidden]}>
        <View style={styles.nameBlockMobile}>
          <Text style={styles.name}>{name}</Text>
          <Text style={styles.role}>{role}</Text>
        </View>

        <View style={[styles.metaRowTight, styles.metaRowCenter]}>
          <View style={styles.icon14}>
            <Feather name="map-pin" size={14} color={colors.muted} />
          </View>
          <Text style={styles.metaText}>{location}</Text>
        </View>

        <View style={[styles.metaRow, styles.metaRowCenter]}>
          <View style={styles.availabilityDot}>
            <AvailabilityIndicator />
          </View>
          <Text style={styles.metaText}>Available for work</Text>
        </View>
      </View>

      <View style={[styles.desktopHeader, isMobile && styles.desktopHeaderHidden]}>
        <View style={styles.headerRow}>
          <View style={styles.profileRow}>
            <Image source={PROFILE_IMAGE} style={styles.avatar} />

            <View style={styles.titleCol}>
              <View style={styles.nameBlock}>
                <Text style={styles.name}>{name}</Text>
                <Text style={styles.role}>{role}</Text>
              </View>

              <View style={styles.metaRowTight}>
                <View style={styles.icon14}>
                  <Feather name="map-pin" size={14} color={colors.muted} />
                </View>
                <Text style={styles.metaText}>{location}</Text>
              </View>

              <View style={styles.metaRow}>
                <View style={styles.availabilityDot}>
                  <AvailabilityIndicator />
                </View>
                <Text style={styles.metaText}>Available for work</Text>
              </View>
            </View>
          </View>
          <View style={styles.actions}>
            <Pressable aria-label="Download CV" onPress={onPressDownloadCv} style={styles.actionLink}>
              <View style={styles.actionRow}>
                <Feather name="download" size={16} color={colors.text} />
                <Text style={styles.actionText}>Download CV</Text>
              </View>
              <View style={styles.actionUnderline} />
            </Pressable>
          </View>
        </View>

        <View style={styles.linksRow}>
          <Pressable aria-label="Copy email" onPress={onCopyEmail} style={styles.emailLink}>
            <Feather name="copy" size={16} color={colors.muted} />
            <Text style={styles.linkText}>{email}</Text>

            {isEmailCopied ? (
              <View pointerEvents="none" style={styles.copyTooltip}>
                <Text style={styles.copyTooltipText}>Copied</Text>
              </View>
            ) : null}
          </Pressable>

          <View style={styles.socials}>
            <Pressable
              aria-label="LinkedIn"
              accessibilityRole="link"
              onPress={() => void onPressLinkedIn()}
              style={styles.socialLink}
            >
              <Feather name="linkedin" size={16} color={colors.muted} />
              <Text style={styles.linkText}>LinkedIn</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingTop: 100,
  },
  mobileIntro: {
    alignItems: 'center',
    width: '100%',
  },
  mobileIntroHidden: {
    display: 'none',
  },
  desktopHeader: {
    display: 'flex',
  },
  desktopHeaderHidden: {
    display: 'none',
  },
  headerRow: {
    alignItems: 'flex-end',
    flexDirection: 'row',
  },
  profileRow: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    marginRight: 16,
    overflow: 'hidden',
  },
  avatar: {
    borderRadius: 8,
    height: 122,
    marginRight: 16,
    width: 122,
  },
  titleCol: {
    flex: 1,
    overflow: 'hidden',
  },
  nameBlock: {
    marginBottom: 16,
  },
  nameBlockMobile: {
    alignItems: 'center',
    marginBottom: 16,
  },
  name: {
    color: colors.text,
    fontFamily: inter500,
    fontSize: 26,
    fontWeight: '500',
    lineHeight: 31.2,
  },
  role: {
    color: colors.muted,
    fontFamily: inter500,
    fontSize: 20,
    fontWeight: '500',
    lineHeight: 24,
    marginTop: 4,
  },
  metaRowTight: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  metaRow: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: 4,
  },
  metaRowCenter: {
    justifyContent: 'center',
  },
  icon14: {
    alignItems: 'center',
    flexShrink: 0,
    height: 26,
    justifyContent: 'center',
    marginRight: 8,
    width: 26,
  },
  metaText: {
    color: colors.muted,
    fontFamily: inter400,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  availabilityDot: {
    alignItems: 'center',
    flexShrink: 0,
    height: 26,
    justifyContent: 'center',
    marginRight: 8,
    overflow: 'visible',
    width: 26,
  },
  actions: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 16,
  },
  actionLink: {
    alignItems: 'center',
    flexDirection: 'column',
    gap: 2,
    overflow: 'hidden',
  },
  actionRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  actionText: {
    color: colors.text,
    fontFamily: inter400,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22.4,
  },
  actionUnderline: {
    alignSelf: 'stretch',
    backgroundColor: colors.border,
    height: 1,
    width: '100%',
  },
  linksRow: {
    borderColor: colors.border,
    borderTopWidth: 1,
    flexDirection: 'row',
    marginTop: 32,
    paddingTop: 8,
  },
  emailLink: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: 6,
    height: 20,
    overflow: 'visible',
    paddingHorizontal: 4,
    position: 'relative',
  },
  socials: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'flex-end',
  },
  socialLink: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  linkText: {
    color: colors.muted,
    fontFamily: inter400,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22.4,
  },
  copyTooltip: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 6,
    borderWidth: 1,
    left: 0,
    paddingHorizontal: 10,
    paddingVertical: 6,
    position: 'absolute',
    top: -36,
    zIndex: 20,
    ...(isWeb
      ? {
        boxShadow: '0px 8px 24px rgba(0,0,0,0.35)',
      }
      : null),
  },
  copyTooltipText: {
    color: colors.text,
    fontFamily: inter500,
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 14.4,
  },
});
